import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  ReactNode,
} from 'react';

type BroadcastContextData = {
  theaterMode: boolean;
  sidebarCollapsed: boolean;
  mobileDrawerOpen: boolean;
  activeTab: string;
  setTheaterMode: (value: boolean) => void;
  setSidebarCollapsed: (value: boolean) => void;
  setMobileDrawerOpen: (value: boolean) => void;
  setActiveTab: (tab: string) => void;
  toggleTheaterMode: () => void;
  toggleSidebar: () => void;
  openMobileDrawer: (tab?: string) => void;
  closeMobileDrawer: () => void;
};

const BroadcastContext = createContext<BroadcastContextData>(
  {} as BroadcastContextData,
);

type BroadcastProviderProps = {
  children: ReactNode;
  initialTab?: string;
};

export const BroadcastProvider = ({
  children,
  initialTab = 'chat',
}: BroadcastProviderProps) => {
  const [theaterMode, setTheaterMode] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(initialTab);

  const toggleTheaterMode = useCallback(() => {
    setTheaterMode((prev) => !prev);
  }, []);

  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed((prev) => !prev);
  }, []);

  const openMobileDrawer = useCallback((tab?: string) => {
    if (tab) {
      setActiveTab(tab);
    }
    setMobileDrawerOpen(true);
  }, []);

  const closeMobileDrawer = useCallback(() => {
    setMobileDrawerOpen(false);
  }, []);

  const value = useMemo(
    () => ({
      theaterMode,
      sidebarCollapsed,
      mobileDrawerOpen,
      activeTab,
      setTheaterMode,
      setSidebarCollapsed,
      setMobileDrawerOpen,
      setActiveTab,
      toggleTheaterMode,
      toggleSidebar,
      openMobileDrawer,
      closeMobileDrawer,
    }),
    [
      theaterMode,
      sidebarCollapsed,
      mobileDrawerOpen,
      activeTab,
      toggleTheaterMode,
      toggleSidebar,
      openMobileDrawer,
      closeMobileDrawer,
    ],
  );

  return (
    <BroadcastContext.Provider value={value}>
      {children}
    </BroadcastContext.Provider>
  );
};

export const useBroadcast = () => {
  const context = useContext(BroadcastContext);

  if (!context) {
    throw new Error('useBroadcast must be used within a BroadcastProvider');
  }

  return context;
};

export default BroadcastContext;
